import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  LayoutDashboard,
  Users,
  FileText,
  CreditCard,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  UserCircle,
  Briefcase,
  DollarSign,
  Menu,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';

const roleNavItems = {
  client: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/client' },
    { icon: UserCircle, label: 'My Profile', path: '/client/profile' },
    { icon: BarChart3, label: 'Credit Scores', path: '/client/scores' },
    { icon: CreditCard, label: 'Payments', path: '/client/payments' },
    { icon: FileText, label: 'Documents', path: '/client/documents' },
  ],
  partner: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/partner' },
    { icon: Users, label: 'My Clients', path: '/partner/clients' },
  ],
  operator: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Clients', path: '/admin/clients' },
    { icon: FileText, label: 'Letters', path: '/admin/letters' },
    { icon: BarChart3, label: 'Scores', path: '/admin/scores' },
  ],
  office_admin: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Clients', path: '/admin/clients' },
    { icon: Briefcase, label: 'Partners', path: '/admin/partners' },
    { icon: FileText, label: 'Letters', path: '/admin/letters' },
    { icon: BarChart3, label: 'Scores', path: '/admin/scores' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ],
  administrative: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Clients', path: '/admin/clients' },
    { icon: Briefcase, label: 'Partners', path: '/admin/partners' },
    { icon: FileText, label: 'Letters', path: '/admin/letters' },
    { icon: BarChart3, label: 'Scores', path: '/admin/scores' },
    { icon: DollarSign, label: 'Finance', path: '/admin/finance' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ],
  aline: [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Users, label: 'Clients', path: '/admin/clients' },
    { icon: Briefcase, label: 'Partners', path: '/admin/partners' },
    { icon: FileText, label: 'Letters', path: '/admin/letters' },
    { icon: BarChart3, label: 'Scores', path: '/admin/scores' },
    { icon: DollarSign, label: 'Finance', path: '/admin/finance' },
    { icon: UserCircle, label: 'Staff & Payroll', path: '/admin/staff' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ],
};

const roleBadgeColors = {
  client: 'bg-emerald-100 text-emerald-800',
  partner: 'bg-blue-100 text-blue-800',
  operator: 'bg-amber-100 text-amber-800',
  office_admin: 'bg-teal-100 text-teal-800',
  administrative: 'bg-purple-100 text-purple-800',
  aline: 'bg-rose-100 text-rose-800',
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = roleNavItems[user?.role] || [];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-lg shadow-lg"
        data-testid="mobile-menu-button"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar Overlay for Mobile */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-40 bg-slate-950 transition-all duration-300 flex flex-col',
          collapsed ? 'w-20' : 'w-64',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        data-testid="dashboard-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {!collapsed && (
            <div className="flex items-center gap-3">
              <img src="/supreme-logo.png" alt="Supreme Credit" className="w-8 h-8 object-contain" />
              <span className="font-semibold text-white">Supreme Credit</span>
            </div>
          )}
          {collapsed && (
            <img src="/supreme-logo.png" alt="SC" className="w-8 h-8 object-contain mx-auto" />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            data-testid="sidebar-collapse-button"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* User Info */}
        <div className={cn('p-4 border-b border-slate-800', collapsed && 'flex justify-center')}>
          {!collapsed ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                <UserCircle className="w-6 h-6 text-slate-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', roleBadgeColors[user?.role])}>
                  {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                </span>
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
              <UserCircle className="w-6 h-6 text-slate-300" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Logout */}
        <div className="p-4 border-t border-slate-800">
          <Button
            variant="ghost"
            className={cn(
              'w-full text-slate-400 hover:text-white hover:bg-slate-800',
              collapsed ? 'justify-center px-0' : 'justify-start'
            )}
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="ml-3">Sign out</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn('flex-1 min-h-screen', mobileOpen ? '' : '')}>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
